import React, { useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, Users, Car, Minus, Plus } from 'lucide-react';
import { format } from 'date-fns';
import LocationInput from './LocationInput';
import { useAddressSuggestions } from './useAddressSuggestions';
import { ALL_VEHICLES } from './constants';
import { TimePicker } from '@/components/ui/time-picker';

const TripDetailsForm = ({ formState, onFormChange }) => {
  const { tripType, date, time, vehicle, passengers, hours, pickup, dropoff } = formState;

  const {
    suggestions: pickupSuggestions,
    isLoading: isPickupLoading,
    fetchSuggestions: fetchPickupSuggestions,
    recordSelection: recordPickupSelection,
  } = useAddressSuggestions();

  const {
    suggestions: dropoffSuggestions,
    isLoading: isDropoffLoading,
    fetchSuggestions: fetchDropoffSuggestions,
    recordSelection: recordDropoffSelection,
  } = useAddressSuggestions();

  const handlePickupChange = (value) => {
    onFormChange('pickup', value);
    if (formState.selectedPickup) onFormChange('selectedPickup', null);
  };

  const handleDropoffChange = (value) => {
    onFormChange('dropoff', value);
    if (formState.selectedDropoff) onFormChange('selectedDropoff', null);
  };

  const handleSelectPickup = (suggestion) => {
    onFormChange('pickup', suggestion.description);
    onFormChange('selectedPickup', suggestion);
    recordPickupSelection(suggestion, formState.pickup || suggestion.description);
  };

  const handleSelectDropoff = (suggestion) => {
    onFormChange('dropoff', suggestion.description);
    onFormChange('selectedDropoff', suggestion);
    recordDropoffSelection(suggestion, formState.dropoff || suggestion.description);
  };

  const handleHoursChange = (amount) => {
    const newHours = hours + amount;
    if (tripType === 'As Directed') {
      onFormChange('hours', Math.max(3, newHours));
    } else {
      onFormChange('hours', Math.max(1, newHours));
    }
  };

  useEffect(() => {
    if (tripType === 'As Directed' && hours < 3) {
      onFormChange('hours', 3);
    }
  }, [tripType, hours, onFormChange]);

  const isLocationBased = tripType === 'One Way' || tripType === 'Wait & Return';
  const isTimeBased = tripType === 'Hourly' || tripType === 'As Directed';

  return (
    <>
      <div className="md:col-span-2">
        <Label htmlFor="tripType" className="text-base sm:text-lg">Trip Type</Label>
        <Select onValueChange={(value) => onFormChange('tripType', value)} value={tripType}>
          <SelectTrigger id="tripType" name="tripType" className="w-full mt-2 h-10 sm:h-12 text-base sm:text-lg"><SelectValue placeholder="Select trip type" /></SelectTrigger>
          <SelectContent>{['One Way', 'Hourly', 'As Directed', 'Wait & Return'].map(type => <SelectItem key={type} value={type} className="text-base sm:text-lg">{type}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLocationBased && (
        <>
          <LocationInput
            id="pickup"
            label="Pickup Location"
            placeholder="e.g., Dulles International Airport"
            value={pickup}
            onChange={handlePickupChange}
            onClear={() => handlePickupChange('')}
            onSelectSuggestion={handleSelectPickup}
            suggestions={pickupSuggestions}
            isLoading={isPickupLoading}
            fetchSuggestions={fetchPickupSuggestions}
            isSelected={!!formState.selectedPickup}
          />
          <LocationInput
            id="dropoff"
            label="Drop-off Location"
            placeholder="e.g., 1600 Pennsylvania Ave NW"
            value={dropoff}
            onChange={handleDropoffChange}
            onClear={() => handleDropoffChange('')}
            onSelectSuggestion={handleSelectDropoff}
            suggestions={dropoffSuggestions}
            isLoading={isDropoffLoading}
            fetchSuggestions={fetchDropoffSuggestions}
            isSelected={!!formState.selectedDropoff}
          />
        </>
      )}

      {isTimeBased && (
        <div className="md:col-span-2">
          <Label htmlFor="hours" className="text-base sm:text-lg">Hours</Label>
          <div className="flex items-center gap-2 sm:gap-4 mt-2">
            <Button type="button" variant="outline" size="icon" onClick={() => handleHoursChange(-1)}><Minus className="h-4 w-4" /></Button>
            <span className="text-lg sm:text-xl font-bold w-12 sm:w-16 text-center">{hours}</span>
            <Button type="button" variant="outline" size="icon" onClick={() => handleHoursChange(1)}><Plus className="h-4 w-4" /></Button>
            {tripType === 'As Directed' && <span className="text-sm sm:text-base text-gray-400">(Minimum 3 hours)</span>}
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="date" className="text-base sm:text-lg">Date & Time</Label>
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <Popover>
            <PopoverTrigger asChild><Button id="date" name="date" variant={"outline"} className="w-full justify-start text-left font-normal h-10 sm:h-12 text-sm sm:text-lg !bg-white !text-gray-900 !border-gray-300 hover:!bg-gray-50 hover:!text-gray-900"><CalendarIcon className="mr-2 h-4 w-4" />{date ? format(date, "PPP") : <span>Pick a date</span>}</Button></PopoverTrigger>
            <PopoverContent className="w-auto p-0 !bg-white !text-gray-900 !border-gray-300"><Calendar mode="single" selected={date} onSelect={(d) => onFormChange('date', d)} initialFocus /></PopoverContent>
          </Popover>
          <TimePicker id="time" name="time" value={time} onChange={(value) => onFormChange('time', value)} />
        </div>
      </div>
      <div>
        <Label htmlFor="vehicle" className="text-base sm:text-lg">Vehicle Type</Label>
        <Select onValueChange={(value) => onFormChange('vehicle', value)} value={vehicle} defaultValue="Luxury SUV">
          <SelectTrigger id="vehicle" name="vehicle" className="w-full mt-2 h-10 sm:h-12 text-sm sm:text-lg"><Car className="mr-2 h-4 w-4" /><SelectValue placeholder="Select vehicle" /></SelectTrigger>
          <SelectContent>{ALL_VEHICLES.map(v => <SelectItem key={v} value={v} className="text-sm sm:text-lg">{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="passengers" className="text-base sm:text-lg">Passengers</Label>
        <Select onValueChange={(val) => onFormChange('passengers', parseInt(val))} value={String(passengers)}>
          <SelectTrigger id="passengers" name="passengers" className="w-full mt-2 h-10 sm:h-12 text-sm sm:text-lg"><Users className="mr-2 h-4 w-4" /><SelectValue placeholder="Select passengers" /></SelectTrigger>
          <SelectContent>{[...Array(10).keys()].map(i => <SelectItem key={i + 1} value={String(i + 1)} className="text-sm sm:text-lg">{i + 1}</SelectItem>)}<SelectItem value="15" className="text-sm sm:text-lg">11-15</SelectItem><SelectItem value="25" className="text-sm sm:text-lg">16-25</SelectItem><SelectItem value="50" className="text-sm sm:text-lg">26-50</SelectItem></SelectContent>
        </Select>
      </div>
    </>
  );
};

export default TripDetailsForm;